import _ from "lodash";
import React from "react";
import classNames from "classnames";

import { Avatar, Checkbox, Tooltip } from "@toddle-edu/ds-web";
import { StudentOutlined, FamilyOutlined } from "@toddle-edu/ds-icons";

import { useI18n } from "Hooks";
import StudentAlerts from "AppComponents/StudentAlerts";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import ConditionalTooltipText from "UIComponents/ConditionalTooltipText";

import { useMetadata } from "../../../MetadataProvider";

import classes from "./UserTypeCard.scss";

const UserTypeCard = ({
  avatarId,
  mainText,
  subText,
  profileImage,
  onAddUsers,
  onRemoveUsers,
  user,
  parents,
  isUserChecked,
  isParentChecked,
  isParentIndeterminate,
  isParentDisabled,
  isUserDisabled,
  testId,
}) => {
  const { t } = useI18n(["common"]);
  const componentTestId = testId ? testId : undefined;
  const {
    disabledUserTooltipText,
    disabledNestedFamilyTooltipText,
  } = useMetadata();

  const onAddUser = () => onAddUsers([user]);
  const onAddParents = () => onAddUsers(parents);
  const onRemoveUser = () => onRemoveUsers([user]);
  const onRemoveParents = () => onRemoveUsers(parents);

  const onUserCheckboxChange = isUserChecked ? onRemoveUser : onAddUser;

  const onParentCheckboxChange =
    isParentChecked || isParentIndeterminate ? onRemoveParents : onAddParents;

  const isStudentType = _.isEqual(_.get(user, "type"), "student");
  const { id, alertTypes = [] } = user;

  const mainTextClasses = classNames(classes.title, "text-body");
  const subTitleClasses = classNames(classes.userSubTitle, "text-body-s");

  return (
    <div className={classes.userTypeCardContainer}>
      <Avatar
        dsVersion="2.0"
        id={avatarId}
        name={mainText}
        profileImage={profileImage}
        size="medium"
        shape="square"
        className={classes.userAvatar}
      />
      <div className={classes.userInfoContainer}>
        <div className={classes.titleContainer}>
          <div className={mainTextClasses}>{mainText}</div>
          {isStudentType ? (
            <StudentAlerts studentId={id} alertTypes={alertTypes} />
          ) : null}
        </div>
        {subText ? (
          <ConditionalTooltipText text={subText} className={subTitleClasses} />
        ) : null}
      </div>
      <div className={classes.checkboxContainer}>
        {!_.isEmpty(parents) ? (
          <div className={classes.checkbox}>
            <div className={classes.parentsIconAndCountContainer}>
              <div className={classNames("text-body-1", classes.parentsCount)}>
                {_.size(parents)}
              </div>
              <Tooltip
                placement="top"
                tooltip={
                  <div className="text-tooltip">
                    {t("common:add_family_members_of_student")}
                  </div>
                }
              >
                <FamilyOutlined variant="subtle" />
              </Tooltip>
            </div>
            <ConditionalWrapper
              condition={isParentDisabled}
              wrapper={
                <Tooltip
                  placement="top"
                  tooltip={t(disabledNestedFamilyTooltipText)}
                />
              }
              type={"wrapperAsElement"}
            >
              <Checkbox
                onChange={onParentCheckboxChange}
                disabled={isParentDisabled}
                isChecked={isParentChecked || isParentDisabled}
                isIndeterminate={isParentIndeterminate}
                testId={componentTestId}
              />
            </ConditionalWrapper>
          </div>
        ) : null}
        <div className={classes.checkbox}>
          {isStudentType ? (
            <Tooltip
              placement="top"
              tooltip={
                <div className="text-tooltip">{t("common:add_student")}</div>
              }
            >
              <StudentOutlined variant="subtle" />
            </Tooltip>
          ) : null}
          <ConditionalWrapper
            condition={isUserDisabled}
            wrapper={
              <Tooltip placement="top" tooltip={t(disabledUserTooltipText)} />
            }
            type={"wrapperAsElement"}
          >
            <Checkbox
              onChange={onUserCheckboxChange}
              isChecked={isUserChecked || isUserDisabled}
              disabled={isUserDisabled}
              testId={componentTestId}
            />
          </ConditionalWrapper>
        </div>
      </div>
    </div>
  );
};

export default UserTypeCard;
